import React, { useEffect, useState, lazy } from 'react';
import { StarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function TestimonialsSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const headerReveal = useScrollReveal({
    threshold: 0.2
  });
  const cardsReveal = useScrollReveal({
    threshold: 0.1
  });
  const testimonials = [{
    text: "I used to hit that 2pm wall every single day. Now? I'm still going strong when my kids get home from school. I have energy to actually play with them instead of just surviving until bedtime. This isn't caffeine jitters — it's real, steady strength.",
    author: 'Sarah M., Mom of 3',
    verified: true,
    rating: 5
  }, {
    text: "My doctor noticed my blood pressure readings before I even mentioned the supplement. 'Whatever you're doing, keep doing it,' she said. I finally feel like my body is working with me, not against me. That's worth everything.",
    author: 'Rachel T., 52',
    verified: true,
    rating: 5
  }, {
    text: "I stopped feeling guilty about needing to rest. My body was depleted — not because I was lazy, but because I was running on empty. Now I wake up feeling restored. Like I actually slept. That hasn't happened in years.",
    author: "Jennifer L., Women's Ministry Leader",
    verified: true,
    rating: 5
  }, {
    text: "I was skeptical about another 'heart health' supplement. But my husband noticed I wasn't as winded going up stairs. My morning walks don't feel like a chore anymore. I actually look forward to them. That's when I knew this was different.",
    author: 'Amanda K., 48',
    verified: true,
    rating: 5
  }, {
    text: 'I finished my entire to-do list yesterday. Not because I was manic or over-caffeinated — I just had the energy to keep going. Steady, sustained energy that lasted all day. I forgot what that felt like.',
    author: 'Amanda T., Working Mom',
    verified: true,
    rating: 5
  }, {
    text: "My husband said I seem 'lighter.' Not physically — emotionally. Like I'm not carrying the weight of exhaustion everywhere I go. We went on a date night and I wasn't fighting to stay awake. I was present. Engaged. Myself again.",
    author: 'Brittany S., Married 11 Years',
    verified: true,
    rating: 5
  }];
  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // Items per slide: 2 on mobile, 3 on desktop
  const itemsPerSlide = isMobile ? 2 : 3;
  const totalSlides = Math.ceil(testimonials.length / itemsPerSlide);
  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, 5000);
    return () => clearInterval(timer);
  }, [totalSlides]);
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  };
  const visibleTestimonials = testimonials.slice(currentSlide * itemsPerSlide, currentSlide * itemsPerSlide + itemsPerSlide);
  return <section className="w-full bg-[#2d4a3a] py-16 lg:py-24 relative overflow-hidden" data-id="element-480">
      {/* Animated Wave Background */}
      <div className="absolute inset-0 opacity-10" data-id="element-481">
        <svg className="w-full h-full" viewBox="0 0 1440 320" preserveAspectRatio="none" data-id="element-482">
          <path fill="currentColor" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" data-id="element-483">
            <animate attributeName="d" dur="10s" repeatCount="indefinite" values="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;
                      M0,160L48,144C96,128,192,96,288,96C384,96,480,128,576,144C672,160,768,160,864,144C960,128,1056,96,1152,96C1248,96,1344,128,1392,144L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;
                      M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" data-id="element-484" />
          </path>
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" data-id="element-485">
        {/* Section Header */}
        <div ref={headerReveal.ref} className={`text-center mb-12 transition-all duration-1000 ${headerReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-486">
          <div className="flex items-center justify-center gap-2 mb-4" data-id="element-487">
            {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-8 h-8 fill-[#d4af37] text-[#d4af37] animate-pulse" style={{
            animationDelay: `${i * 0.1}s`
          }} data-id="element-488" />)}
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-489">
            Join 1,000,000+ Customers Who Love Faith & Fig
          </h2>
          <p className="text-white/80 text-lg" data-id="element-490">
            Real Christian women. Real results. Real strength.
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative" data-id="element-491">
          <div ref={cardsReveal.ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-492">
            {visibleTestimonials.map((testimonial, index) => <div key={index} className={`bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1 ${cardsReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{
            transitionDelay: `${index * 150}ms`,
            transitionDuration: '700ms'
          }} data-id="element-493">
                <div className="flex items-center gap-1 mb-4" data-id="element-494">
                  {[...Array(testimonial.rating)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-[#d4af37] text-[#d4af37]" data-id="element-495" />)}
                </div>
                <p className="text-gray-700 leading-relaxed mb-4 text-sm" data-id="element-496">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center justify-between" data-id="element-497">
                  <p className="font-semibold text-[#2d4a3a] text-sm" data-id="element-498">
                    {testimonial.author}
                  </p>
                  {testimonial.verified && <span className="text-xs text-[#496f58] font-medium" data-id="element-499">
                      ✓ Verified Customer
                    </span>}
                </div>
              </div>)}
          </div>

          {/* Carousel Controls */}
          <div className="flex items-center justify-center gap-4" data-id="element-500">
            <button onClick={prevSlide} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-all" aria-label="Previous testimonials" data-id="element-501">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-502" />
            </button>

            {/* Dots Indicator */}
            <div className="flex gap-2" data-id="element-503">
              {Array.from({
              length: totalSlides
            }).map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-2 h-2 rounded-full transition-all ${currentSlide === index ? 'bg-white w-8' : 'bg-white/40'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-504" />)}
            </div>

            <button onClick={nextSlide} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-all" aria-label="Next testimonials" data-id="element-505">
              <ChevronRightIcon className="w-5 h-5" data-id="element-506" />
            </button>
          </div>
        </div>

        {/* Trust Badges */}
      </div>
    </section>;
}